import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Breadcrumb, Button, Card, Empty, Spin, Table } from "antd";
import { DoubleLeftOutlined } from "@ant-design/icons";
import { Link, useLocation, useParams } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import KycService from "../../services/kyc-service";
import {
	buildKycCompanyTabUrl,
	buildKycDashboardReturnLink,
	buildKycMaterialQuotationTabReturn,
	buildKycMaterialsTableSearchReturn,
	buildKycPartnerFunctionQuotationTabReturn,
	buildKycPartnerFunctionTabReturn,
	buildKycTableSearchUrl,
	buildKycTileCountsPayloadWithScope,
	getCylinderTileCounts,
	kycBreadcrumbLabelsMatch,
	loadKycQuotationNavContext,
	saveKycQuotationNavContext,
	getKycAppliedFilterPayload,
} from "./kycHelpers";
import { KYC_DEFAULT_FILTER_PAYLOAD } from "./kycDesignShared";
import { KYCCylindersImage } from "../Elite/imageAssets";
import "./KYC-Dashboard.css";

const getRawValue = (record, key) => {
	const value = record?.[key];
	if (value !== undefined && value !== null && value !== "") {
		return value;
	}
	return "";
};

const getDisplayValue = (record, key) => {
	const value = getRawValue(record, key);
	return value !== "" ? value : "-";
};

const parseAmount = (value) => {
	if (value === undefined || value === null || value === "") {
		return 0;
	}
	const numeric = Number(String(value).replace(/[^0-9.-]/g, ""));
	return Number.isNaN(numeric) ? 0 : numeric;
};

const formatAmount = (value) => {
	const numeric = parseAmount(value);
	return numeric.toLocaleString("en-IN", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

const extractDetailList = (responseData) => {
	if (Array.isArray(responseData)) {
		return responseData;
	}

	return (
		responseData?.quotationDetailLists ||
		responseData?.quotationDetailList ||
		responseData?.quotationDetails ||
		responseData?.kycQuotationDetailLists ||
		responseData?.items ||
		responseData?.data ||
		[]
	);
};

const extractHeader = (responseData, fallbackHeader) => {
	if (!responseData || Array.isArray(responseData)) {
		return fallbackHeader || {};
	}

	return (
		responseData.quotationMaster ||
		responseData.quotationHeader ||
		responseData.header ||
		responseData.quotation ||
		fallbackHeader ||
		{}
	);
};

const tableEmptyText = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No data found" />;

const getFirstAvailableValue = (...values) =>
	values.find((value) => value !== undefined && value !== null && value !== "");

const hasDisplayCount = (value) => {
	if (value === undefined || value === null || value === "") {
		return false;
	}
	const numericValue = Number(value);
	if (!Number.isNaN(numericValue) && numericValue === 0) {
		return false;
	}
	return true;
};

const getCardTitle = (countObj, label, keys = ["totalCount", "total"]) => {
	const countValue = getFirstAvailableValue(...keys.map((key) => countObj?.[key]));
	return hasDisplayCount(countValue) ? `${countValue} ${label}` : `- ${label}`;
};

const getCardCountValue = (countObj, keys = []) => {
	if (!countObj || Object.keys(countObj).length === 0) {
		return "-";
	}
	const countValue = getFirstAvailableValue(...keys.map((key) => countObj?.[key]));
	return hasDisplayCount(countValue) ? countValue : "-";
};

const buildQuotationDetailsTilePayload = (quotationId) => ({
	...KYC_DEFAULT_FILTER_PAYLOAD,
	...getKycAppliedFilterPayload(),
	quotationId: Number(quotationId) || quotationId,
});

const KycQuotationDetails = () => {
	const { quotationId } = useParams();
	const location = useLocation();
	const [isLoading, setIsLoading] = useState(false);
	const [isTilesLoading, setIsTilesLoading] = useState(false);
	const [lineItems, setLineItems] = useState([]);
	const [headerData, setHeaderData] = useState({});
	const [totalAmount, setTotalAmount] = useState(0);
	const [cylindersCount, setCylindersCount] = useState({});

	const resolvedQuotationId = useMemo(() => {
		const fromParam = quotationId ? String(quotationId).trim() : "";
		if (fromParam) {
			return fromParam;
		}
		return location.state?.quotationId ? String(location.state.quotationId).trim() : "";
	}, [quotationId, location.state?.quotationId]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [resolvedQuotationId]);

	const navContext = useMemo(() => {
		const fromState = location.state || {};
		const stored = loadKycQuotationNavContext(resolvedQuotationId);
		return {
			quotationNumber:
				fromState.quotationNumber || stored.quotationNumber || resolvedQuotationId || "Quotation",
			groupCompanyId: fromState.groupCompanyId || stored.groupCompanyId || "",
			groupCompanyName: fromState.groupCompanyName || stored.groupCompanyName || "",
			returnSource: fromState.returnSource || stored.returnSource || "dashboard",
			materialProductName: fromState.materialProductName || stored.materialProductName || "",
			materialName: fromState.materialName || stored.materialName || "",
			materialsCustomer: fromState.materialsCustomer || stored.materialsCustomer || "",
			accountName: fromState.accountName || stored.accountName || "",
			returnTab: fromState.returnTab || stored.returnTab || "quotation",
			partnerCrmGuid: fromState.partnerCrmGuid || stored.partnerCrmGuid || "",
			customer: fromState.customer || stored.customer || "",
		};
	}, [location.state, resolvedQuotationId]);

	const isMaterialReturn =
		navContext.returnSource === "material" && Boolean(navContext.materialProductName);
	const isPartnerReturn =
		navContext.returnSource === "partner" && Boolean(navContext.partnerCrmGuid);

	useEffect(() => {
		if (!resolvedQuotationId) {
			return;
		}

		saveKycQuotationNavContext(resolvedQuotationId, {
			quotationNumber: navContext.quotationNumber,
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: navContext.groupCompanyName,
			returnSource: navContext.returnSource,
			materialProductName: navContext.materialProductName,
			materialName: navContext.materialName,
			materialsCustomer: navContext.materialsCustomer,
			accountName: navContext.accountName,
			returnTab: navContext.returnTab,
			partnerCrmGuid: navContext.partnerCrmGuid,
			customer: navContext.customer,
		});
	}, [resolvedQuotationId, navContext]);

	const quotationNumberLabel = useMemo(() => {
		const fromHeader =
			getRawValue(headerData, "quotationNo") || getRawValue(headerData, "quotationNumber");
		if (fromHeader) {
			return fromHeader;
		}
		const fromState = location.state?.quotationNumber;
		if (fromState && String(fromState).trim()) {
			return String(fromState).trim();
		}
		if (
			navContext.quotationNumber &&
			String(navContext.quotationNumber).trim() &&
			navContext.quotationNumber !== "Quotation"
		) {
			return String(navContext.quotationNumber).trim();
		}
		return resolvedQuotationId || "Quotation";
	}, [headerData, location.state?.quotationNumber, resolvedQuotationId, navContext.quotationNumber]);

	const dashboardQuotationTabLink = buildKycCompanyTabUrl("quotation");

	const materialQuotationTabReturn = useMemo(
		() =>
			buildKycMaterialQuotationTabReturn({
				productName: navContext.materialProductName,
				materialName: navContext.materialName || navContext.materialProductName,
				groupCompanyId: navContext.groupCompanyId,
				groupCompanyName: navContext.groupCompanyName,
				materialsCustomer: navContext.materialsCustomer,
				accountName: navContext.accountName,
				returnTab: navContext.returnTab,
			}),
		[navContext]
	);

	const dashboardMaterialsReturn = useMemo(
		() =>
			buildKycMaterialsTableSearchReturn({
				materialName: navContext.materialName || navContext.materialProductName,
				groupCompanyId: navContext.groupCompanyId,
				groupCompanyName: navContext.groupCompanyName,
				materialsCustomer: navContext.materialsCustomer,
				accountName: navContext.accountName,
			}),
		[navContext]
	);

	const partnerFunctionTabReturn = useMemo(
		() =>
			buildKycPartnerFunctionTabReturn({
				crmGuid: navContext.partnerCrmGuid,
				partnerTab: "partnerFunction",
				groupCompanyId: navContext.groupCompanyId,
				groupCompanyName: navContext.groupCompanyName,
				accountName: navContext.accountName,
				materialsCustomer: navContext.materialsCustomer,
				returnTab: navContext.returnTab,
			}),
		[navContext]
	);

	const partnerQuotationTabReturn = useMemo(
		() =>
			buildKycPartnerFunctionQuotationTabReturn({
				crmGuid: navContext.partnerCrmGuid,
				groupCompanyId: navContext.groupCompanyId,
				groupCompanyName: navContext.groupCompanyName,
				accountName: navContext.accountName,
				materialsCustomer: navContext.materialsCustomer,
				returnTab: navContext.returnTab,
			}),
		[navContext]
	);

	const groupCompanyBreadcrumbLabel = (navContext.groupCompanyName || "").trim();
	const accountBreadcrumbLabel = (navContext.accountName || "").trim();
	const materialsBreadcrumbLabel = (navContext.materialsCustomer || "").trim();
	const normalizedGroupLabel = groupCompanyBreadcrumbLabel.toLowerCase();
	const normalizedAccountLabel = accountBreadcrumbLabel.toLowerCase();
	const hasDistinctGroupAndAccount =
		Boolean(normalizedGroupLabel && normalizedAccountLabel) &&
		normalizedGroupLabel !== normalizedAccountLabel;
	const hasDuplicateGroupAndAccount =
		Boolean(normalizedGroupLabel && normalizedAccountLabel) &&
		normalizedGroupLabel === normalizedAccountLabel;
	const showPartnerGroupInBreadcrumb = Boolean(
		groupCompanyBreadcrumbLabel &&
			(navContext.returnTab === "materials" || hasDistinctGroupAndAccount)
	);
	const showPartnerAccountInBreadcrumb = Boolean(
		accountBreadcrumbLabel &&
			navContext.returnTab === "account" &&
			(hasDistinctGroupAndAccount ||
				hasDuplicateGroupAndAccount ||
				!groupCompanyBreadcrumbLabel)
	);
	const showPartnerMaterialsInBreadcrumb =
		navContext.returnTab === "materials" && Boolean(materialsBreadcrumbLabel);
	const groupAndAccountNamesMatch = kycBreadcrumbLabelsMatch(
		groupCompanyBreadcrumbLabel,
		showPartnerAccountInBreadcrumb ? accountBreadcrumbLabel : ""
	);

	const dashboardGroupCompanyLink = useMemo(() => {
		if (!showPartnerGroupInBreadcrumb) {
			return "/kyc";
		}
		return buildKycDashboardReturnLink({
			returnTab: "groupCompany",
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: groupCompanyBreadcrumbLabel,
		});
	}, [showPartnerGroupInBreadcrumb, groupCompanyBreadcrumbLabel, navContext.groupCompanyId]);

	const dashboardAccountLink = useMemo(() => {
		if (!showPartnerAccountInBreadcrumb) {
			return buildKycCompanyTabUrl("account");
		}
		return buildKycDashboardReturnLink({
			returnTab: "account",
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: groupCompanyBreadcrumbLabel,
			accountName: accountBreadcrumbLabel,
		});
	}, [
		showPartnerAccountInBreadcrumb,
		accountBreadcrumbLabel,
		groupCompanyBreadcrumbLabel,
		navContext.groupCompanyId,
	]);

	const dashboardMaterialsDrillAccountLink = useMemo(() => {
		if (!showPartnerMaterialsInBreadcrumb) {
			return null;
		}
		return buildKycDashboardReturnLink({
			returnTab: "account",
			groupCompanyId: navContext.groupCompanyId,
			groupCompanyName: showPartnerGroupInBreadcrumb ? groupCompanyBreadcrumbLabel : "",
			accountName: materialsBreadcrumbLabel,
		});
	}, [
		showPartnerMaterialsInBreadcrumb,
		showPartnerGroupInBreadcrumb,
		groupCompanyBreadcrumbLabel,
		materialsBreadcrumbLabel,
		navContext.groupCompanyId,
	]);

	const dashboardQuotationBackLink = useMemo(() => {
		if (isPartnerReturn) {
			return partnerQuotationTabReturn.link;
		}
		if (isMaterialReturn) {
			return materialQuotationTabReturn.link;
		}
		const search =
			quotationNumberLabel && quotationNumberLabel !== "Quotation"
				? String(quotationNumberLabel).trim()
				: "";
		if (search) {
			return buildKycTableSearchUrl(search, { companyTab: "quotation" });
		}
		return dashboardQuotationTabLink;
	}, [
		isPartnerReturn,
		isMaterialReturn,
		partnerQuotationTabReturn.link,
		materialQuotationTabReturn.link,
		quotationNumberLabel,
		dashboardQuotationTabLink,
	]);

	const dashboardQuotationBackState = useMemo(() => {
		if (isPartnerReturn) {
			return partnerQuotationTabReturn.state;
		}
		if (isMaterialReturn) {
			return materialQuotationTabReturn.state;
		}
		const search =
			quotationNumberLabel && quotationNumberLabel !== "Quotation"
				? String(quotationNumberLabel).trim()
				: "";
		if (!search) {
			return { returnTab: "quotation" };
		}
		return {
			returnTab: "quotation",
			quotationNumber: search,
			tableSearch: search,
		};
	}, [
		isPartnerReturn,
		isMaterialReturn,
		partnerQuotationTabReturn.state,
		materialQuotationTabReturn.state,
		quotationNumberLabel,
	]);

	const materialBreadcrumbLabel =
		navContext.materialName || navContext.materialProductName || "Material";

	useEffect(() => {
		if (!resolvedQuotationId) {
			return undefined;
		}

		setIsLoading(true);
		const requestData = {
			quotationId: Number(resolvedQuotationId) || resolvedQuotationId,
		};

		KycService.getKycQuotationDetailListAPI(requestData)
			.then((response) => {
				const success = response.data?.statusCode === 200 || response.data?.status === true;
				if (success) {
					const responseData = response.data?.responseData || {};
					const items = extractDetailList(responseData);
					const header = extractHeader(responseData, location.state || {});
					const resolvedItems = Array.isArray(items) ? items : [];

					setLineItems(resolvedItems);
					setHeaderData(header || {});

					const apiTotal = getRawValue(responseData, "totalAmount");
					const rowTotal = resolvedItems.length ? getRawValue(resolvedItems[0], "totalAmount") : "";
					if (apiTotal !== "") {
						setTotalAmount(parseAmount(apiTotal));
					} else if (rowTotal !== "") {
						setTotalAmount(parseAmount(rowTotal));
					} else {
						const computed = resolvedItems.reduce(
							(sum, item) => sum + parseAmount(getRawValue(item, "amount")),
							0
						);
						setTotalAmount(computed);
					}
				} else {
					NotificationManager.error(response.data?.message || "Unable to load quotation details.");
					setLineItems([]);
					setHeaderData(location.state || {});
					setTotalAmount(0);
				}
			})
			.catch((error) => {
				console.log(error, "Error");
				NotificationManager.error("Unable to load quotation details.");
				setLineItems([]);
				setHeaderData(location.state || {});
				setTotalAmount(0);
			})
			.finally(() => {
				setIsLoading(false);
			});

		return undefined;
	}, [resolvedQuotationId]);

	useEffect(() => {
		if (!resolvedQuotationId) {
			return undefined;
		}

		setIsTilesLoading(true);
		KycService.getKycTileCountsAPI(
			buildKycTileCountsPayloadWithScope(buildQuotationDetailsTilePayload(resolvedQuotationId), {
				quotationId: resolvedQuotationId,
			})
		)
			.then((response) => {
				if (response.data?.statusCode === 200) {
					const apiData = response.data.responseData || {};
					setCylindersCount(getCylinderTileCounts(apiData));
				}
			})
			.catch((error) => {
				console.log(error, "Error");
			})
			.finally(() => {
				setIsTilesLoading(false);
			});

		return undefined;
	}, [resolvedQuotationId]);

	const lineItemColumns = [
		{
			title: "Material",
			dataIndex: "material",
			key: "material",
			width: 220,
			render: (_, record) => <span>{getDisplayValue(record, "material")}</span>,
		},
		{
			title: "Characteristics",
			dataIndex: "characteristics",
			key: "characteristics",
			width: 220,
			render: (_, record) => <span>{getDisplayValue(record, "characteristics")}</span>,
		},
		{
			title: "MOQ",
			dataIndex: "moq",
			key: "moq",
			width: 120,
			render: (_, record) => <span>{getDisplayValue(record, "moq")}</span>,
		},
		{
			title: "Quantity",
			dataIndex: "quantity",
			key: "quantity",
			width: 120,
			render: (_, record) => <span>{getDisplayValue(record, "quantity")}</span>,
		},
		{
			title: "Base Value",
			dataIndex: "baseValue",
			key: "baseValue",
			width: 130,
			render: (_, record) => <span>{getDisplayValue(record, "baseValue")}</span>,
		},
		{
			title: "Sales Unit",
			dataIndex: "salesUnit",
			key: "salesUnit",
			width: 120,
			render: (_, record) => <span>{getDisplayValue(record, "salesUnit")}</span>,
		},
		{
			title: "Rate",
			dataIndex: "rate",
			key: "rate",
			width: 140,
			render: (_, record) => <span>{getDisplayValue(record, "rate")}</span>,
		},
		{
			title: "Amount",
			dataIndex: "amount",
			key: "amount",
			width: 150,
			render: (_, record) => <span>{getDisplayValue(record, "amount")}</span>,
		},
		{
			title: "Unit Conversion",
			dataIndex: "unitConversion",
			key: "unitConversion",
			width: 160,
			render: (_, record) => <span>{getDisplayValue(record, "unitConversion")}</span>,
		},
		{
			title: "Convert To Order",
			dataIndex: "convertToOrder",
			key: "convertToOrder",
			width: 160,
			render: (_, record) => {
				const value = getRawValue(record, "convertToOrder");
				if (value === "") {
					return <span>-</span>;
				}
				if (value === true || String(value).toLowerCase() === "true") {
					return <span>Yes</span>;
				}
				if (value === false || String(value).toLowerCase() === "false") {
					return <span>No</span>;
				}
				return <span>{value}</span>;
			},
		},
	];

	return (
		<Fragment>
			<div className="dashboard app-content content kyc-quotation-details-page">
				<div className="content-wrapper">
					<div className="content-body">
						<div className="d-flex justify-content-between align-items-center details_view_title mb-2">
							<Breadcrumb className="details_breadcrum" separator=">">
								<Breadcrumb.Item>
									<Link to="/kyc">Dashboard</Link>
								</Breadcrumb.Item>
								{isPartnerReturn ? (
									<>
										{showPartnerGroupInBreadcrumb ? (
											<Breadcrumb.Item>
												<Link to={dashboardGroupCompanyLink}>
													<label>{groupCompanyBreadcrumbLabel}</label>
												</Link>
											</Breadcrumb.Item>
										) : null}
										{showPartnerMaterialsInBreadcrumb ? (
											<Breadcrumb.Item>
												<Link to={dashboardMaterialsDrillAccountLink}>
													<label>{materialsBreadcrumbLabel}</label>
												</Link>
											</Breadcrumb.Item>
										) : null}
										{showPartnerAccountInBreadcrumb && !groupAndAccountNamesMatch ? (
											<Breadcrumb.Item>
												<Link to={dashboardAccountLink}>
													<label>{accountBreadcrumbLabel}</label>
												</Link>
											</Breadcrumb.Item>
										) : null}
										<Breadcrumb.Item>
											<Link
												to={partnerFunctionTabReturn.link}
												state={partnerFunctionTabReturn.state}>
												<label>Partner Function</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<Link
												to={dashboardQuotationBackLink}
												state={dashboardQuotationBackState}>
												<label>Quotation</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<label>{quotationNumberLabel}</label>
										</Breadcrumb.Item>
									</>
								) : isMaterialReturn ? (
									<>
										<Breadcrumb.Item>
											<Link
												to={dashboardMaterialsReturn.link}
												state={dashboardMaterialsReturn.state}>
												<label>{materialBreadcrumbLabel}</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<Link
												to={dashboardQuotationBackLink}
												state={dashboardQuotationBackState}>
												<label>Quotation</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<label>{quotationNumberLabel}</label>
										</Breadcrumb.Item>
									</>
								) : (
									<>
										<Breadcrumb.Item>
											<Link to={dashboardQuotationTabLink}>
												<label>Quotation</label>
											</Link>
										</Breadcrumb.Item>
										<Breadcrumb.Item>
											<label>{quotationNumberLabel}</label>
										</Breadcrumb.Item>
									</>
								)}
							</Breadcrumb>
							<Link to={dashboardQuotationBackLink} state={dashboardQuotationBackState}>
								<Button type="primary" className="back_btn" icon={<DoubleLeftOutlined />}>
									Back
								</Button>
							</Link>
						</div>

						<Spin spinning={isTilesLoading} wrapperClassName="kyc-mc-tiles-spin">
							<div className="row mb-3">
								<div className="col-xl-4 col-lg-6 col-12">
									<div className="card kycbg_purple kyc-design-detail-tile">
										<div className="card-content">
											<div className="kyc-card-body KYCCylinders images_bg">
												<div className="kyc-card-header">
													<div className="kyc-card-title">
														<img src={KYCCylindersImage} alt="Img" />
														<span>{getCardTitle(cylindersCount, "Cylinders")}</span>
													</div>
												</div>
												<div className="kyc-card-stats">
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{getCardCountValue(cylindersCount, ["runningCount", "running"])}
														</h5>
														<h6 className="text-white mb-0">Running</h6>
													</div>
													<div className="kyc-border-right-white border-right-lighten-3" />
													<div className="card_counts text-left">
														<h5 className="text-white mb-2">
															{getCardCountValue(cylindersCount, [
																"hiddenCount",
																"hide",
																"close",
															])}
														</h5>
														<h6 className="text-white mb-0">Close</h6>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</Spin>

						<Spin spinning={isLoading}>
							<Card className="kyc-table-card kyc-quotation-items-card">
								<div className="kyc-quotation-items-header">
									<h5 className="kyc-company-title kyc-company-quote-title">Quotation</h5>
									<span className="kyc-quotation-total-amount">
										Total Amount: {formatAmount(totalAmount)}
									</span>
								</div>
								<Table
									bordered
									scroll={{ x: 1400 }}
									className="logistic_tables kyc_logistic_tables kyc_quotation_items_table"
									dataSource={lineItems}
									columns={lineItemColumns}
									rowKey={(record, index) => index}
									locale={{ emptyText: tableEmptyText }}
									pagination={{
										pageSize: 10,
										responsive: true,
										showSizeChanger: false,
										hideOnSinglePage: false,
										showTotal: (total, range) =>
											total
												? `Showing ${range[0]}-${range[1]} of ${total.toLocaleString()} records`
												: "Showing 0 records",
									}}
								/>
							</Card>
						</Spin>
					</div>
				</div>
			</div>
		</Fragment>
	);
};

export default KycQuotationDetails;
